/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { useLazyQuery, useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import {
  GridOff as GridOffIcon,
  GridOn as GridOnIcon,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { isNil, uniq } from 'lodash'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import ImageWithBoundingBoxes from '@/components/bounding-box/ImageWithBoundingBoxes'
import CenteredBox from '@/components/CenteredBox'
import { Colors } from '@/components/Colors'
import VennDiagram from '@/components/VennDiagram'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_DISPLAY_SNAPSHOTS,
  GET_PRODUCTS,
  GetDisplaySnapshotsQuery,
  GetDisplaySnapshotsQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  ProcessStatus,
  ProductFragment,
} from '@/graphql'
import useT from '@/i18n/useT'
import { getRAGColor, mockThresholds } from '@/mocks/mockStoresReportTable'
import { getIncorrectShelfBoundingBoxes } from '@/utils/compliance.helpers'
import { getFullSizeImage } from '@/utils/image.helpers'
import { ProductHelpers } from '@/utils/products.helpers'

import RenderedPlanogram from '../planogram/components/RenderedPlanogram'

const getProductImageUrl = (product?: ProductFragment | null) =>
  product?.planogramImageUrl || product?.marketingImageUrl || null

type ComplianceProductListItemProps = {
  count: number
  label: string
  imageUrl?: string | null
}

const ComplianceProductListItem: React.FC<ComplianceProductListItemProps> = ({
  count,
  label,
  imageUrl,
}) => (
  <Box
    component="li"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 0.25,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 42,
        height: 42,
        flexShrink: 0,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={getFullSizeImage(imageUrl)}
          alt={label}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        />
      )}
      <Box
        aria-label={`x${count}`}
        sx={{
          minWidth: 18,
          height: 18,
          p: 1,
          borderRadius: 1,
          bgcolor: 'grey.900',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        x{count}
      </Box>
    </Box>
    <Typography component="span" variant="body2">
      {label}
    </Typography>
  </Box>
)

const complianceProductListSx = {
  listStyle: 'none',
  p: 0,
  m: 0,
} as const

const SnapshotCompliance: React.FC = () => {
  const { t } = useT()
  const { snapshotId } = useParams()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const [showBoxes, setShowBoxes] = useState(true)

  const { data, loading, error, refetch } = useQuery<
    GetDisplaySnapshotsQuery,
    GetDisplaySnapshotsQueryVariables
  >(GET_DISPLAY_SNAPSHOTS, {
    variables: {
      filters: {
        id: snapshotId,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })
  useRefresh(refetch)

  const snapshot = data?.tenantDisplaySnapshots?.results?.[0]

  const [getProducts, { data: productsData, loading: productsLoading }] =
    useLazyQuery<GetProductsQuery, GetProductsQueryVariables>(GET_PRODUCTS)

  const upcs: string[] = uniq([
    ...(snapshot?.planogramSegmentShapedFacings?.flatMap(shelf =>
      shelf.flatMap(facing => facing.upc),
    ) || []),
    ...(snapshot?.images?.flatMap(
      image =>
        image.boxSet?.boundingBoxes
          .flatMap(box => box.classifiedLabel)
          .filter((label): label is string => !isNil(label)) || [],
    ) || []),
  ]).sort()

  useEffect(() => {
    if (upcs.length > 0) {
      getProducts({
        variables: {
          filters: {
            upcs: uniq(upcs),
          },
        },
      })
    }
  }, [upcs.join(',')])

  if (loading || productsLoading) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  if (error) {
    return (
      <CenteredBox>
        <Alert severity="error">{error.message}</Alert>
      </CenteredBox>
    )
  }

  if (!snapshot) {
    return (
      <CenteredBox>
        <Alert severity="error">{t('snapshotCompliance.noSnapshot')}</Alert>
      </CenteredBox>
    )
  }

  if (!snapshot.planogramSegmentShapedFacings.length) {
    return (
      <CenteredBox>
        <Alert severity="warning">{t('snapshotCompliance.noPlanogram')}</Alert>
      </CenteredBox>
    )
  }

  const image = snapshot.images[0]
  const imageProcessStatus = image?.process?.status || ProcessStatus.PENDING

  if (imageProcessStatus === ProcessStatus.PENDING) {
    return (
      <CenteredBox>
        <Alert severity="info">{t('snapshotCompliance.processing')}</Alert>
      </CenteredBox>
    )
  }

  if (!snapshot.score || !snapshot.complianceInfo) {
    return (
      <CenteredBox>
        <Alert severity="error">{t('snapshotCompliance.missingData')}</Alert>
      </CenteredBox>
    )
  }

  const complianceBoxes =
    snapshot.images[0]?.boxSet?.boundingBoxes.filter(
      box => box.classifiedLabel !== ProductHelpers.PRICING_UPC,
    ) || []

  const incorrectShelfBoundingBoxIds = new Set(
    getIncorrectShelfBoundingBoxes(
      snapshot.planogramSegmentShapedFacings,
      complianceBoxes,
    ).map(b => b.id),
  )

  const planogramProducts: (ProductFragment & {
    missing: boolean
  })[][] = snapshot.planogramSegmentShapedFacings.map(shelf =>
    shelf
      .map(facing => {
        const product = productsData?.products?.results.find(
          product => product.upcA === facing.upc,
        )
        return product
          ? {
              ...product,
              missing: snapshot?.complianceInfo?.missingFacings.some(
                ({ id }) => id === facing.id,
              ),
            }
          : null
      })
      .filter(
        (product): product is ProductFragment & { missing: boolean } =>
          !isNil(product),
      ),
  )

  const username =
    snapshot.user?.firstName && snapshot.user?.lastName
      ? `${snapshot.user?.firstName} ${snapshot.user?.lastName}`
      : snapshot.user?.email || 'Unknown'
  const storeId = snapshot.display.store?.id || null

  const productsByUpc = Object.fromEntries(
    (productsData?.products?.results || []).map(product => [
      product.upcA,
      product,
    ]),
  ) as Record<string, ProductFragment>

  const getMissingProductLabel = (
    product: ProductFragment | undefined,
    upc: string,
  ) => {
    if (product?.preferredName && product?.packagingName) {
      return `${product.preferredName} ${product.packagingName}`
    }

    return (
      product?.preferredName || product?.name || product?.packagingName || upc
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h1">{snapshot.display.name}</Typography>
        {storeId && (
          <Link
            onClick={() =>
              handleNavTo(
                `/stores/${storeId}/planograms/${snapshot.display.id}`,
                {
                  resetParams: false,
                },
              )
            }
            sx={{
              cursor: 'pointer',
              color: 'error.main',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 'medium',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('snapshotCompliance.viewPlanogram')}
          </Link>
        )}
      </Box>
      <Paper
        sx={{
          p: 6,
        }}
      >
        <Typography variant="h1">
          {t('snapshotCompliance.complianceScore')}
        </Typography>
        <Box
          sx={{
            flex: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            display: 'flex',
            width: '100%',
            gap: { xs: 4, sm: 12 },
            p: 6,
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h1"
            fontSize={50}
            color={getRAGColor(
              mockThresholds.compliance,
              snapshot.score?.score,
            )}
          >
            {`${Math.round((snapshot?.score?.score || 0) * 100)}%`}
          </Typography>
          <VennDiagram
            area1={snapshot.complianceInfo.planogramUpcSet.length}
            area2={snapshot.complianceInfo.detectedUpcSet.length}
            intersectionPercent={
              (snapshot.complianceInfo.intersectionUpcSet.length /
                snapshot.complianceInfo.planogramUpcSet.length) *
              100
            }
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={snapshot.user?.profileImage?.url || undefined}
            alt={username}
          />
          <Box>
            <Typography variant="h5">{username}</Typography>
            <Typography>
              {dayjs(snapshot.captureTime).format('MMMM D, YYYY @ h:mm A')}
            </Typography>
          </Box>
        </Box>
      </Paper>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          p: 6,
          borderRadius: 2,
        }}
      >
        <Typography variant="h1">
          {t('snapshotCompliance.comparison')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            gap: { xs: 4, sm: 12 },
          }}
        >
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="h3">
              {t('snapshotCompliance.headers.planogram')}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h3">
              {t('snapshotCompliance.headers.image')}
            </Typography>
            <IconButton size="small" onClick={() => setShowBoxes(!showBoxes)}>
              {showBoxes ? <GridOffIcon /> : <GridOnIcon />}
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: { xs: 4, sm: 8 },
            pr: { xs: 0, sm: 4 },
            pl: { xs: 0, sm: 4 },
            height: '100%',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                p: 2,
                backgroundColor: Colors.PLANGORAM,
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxSizing: 'border-box',
              }}
            >
              <RenderedPlanogram
                products={planogramProducts}
                shelfHeight={128}
                productInfoByUpc={productsByUpc}
              />
            </Box>
          </Box>
          {snapshot.images[0].url && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  width: '100%',
                  maxWidth: 760,
                  backgroundColor: Colors.REALOGRAM,
                  display: 'flex',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                }}
              >
                <ImageWithBoundingBoxes
                  showBoxes={showBoxes}
                  style={{ width: '100%' }}
                  imageStyle={{ width: '100%', maxHeight: undefined }}
                  status={imageProcessStatus}
                  errorMessage={snapshot.images[0].process?.error}
                  source={getFullSizeImage(snapshot.images[0].url)}
                  boxes={complianceBoxes.map(box => ({
                    ...box,
                    color:
                      box.classifiedLabel === ProductHelpers.EMPTY_SLOT_UPC
                        ? Colors.OOS
                        : box.classifiedLabel === ProductHelpers.UNKNOWN_UPC
                          ? Colors.UNIDENTIFIED
                          : incorrectShelfBoundingBoxIds.has(box.id)
                            ? Colors.EXTRA
                            : Colors.CORRECT,
                  }))}
                  productInfoByUpc={productsByUpc}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 6,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {snapshot.complianceInfo.missingFacings.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                flex: 1,
                border: `10px solid ${Colors.OOS}`,
              }}
            >
              <Typography variant="h5">
                {t('snapshotCompliance.missing', {
                  count: snapshot.complianceInfo.missingFacings.length,
                })}
              </Typography>
              <Box component="ul" sx={complianceProductListSx}>
                {Object.entries(
                  snapshot.complianceInfo.missingFacings.reduce(
                    (acc, item) => {
                      acc[item.upc] = (acc[item.upc] || 0) + 1
                      return acc
                    },
                    {} as Record<string, number>,
                  ),
                ).map(([upc, count]) => {
                  const product = productsByUpc[upc]
                  const label = getMissingProductLabel(product, upc)

                  return (
                    <ComplianceProductListItem
                      key={upc}
                      count={count}
                      label={label}
                      imageUrl={getProductImageUrl(product)}
                    />
                  )
                })}
              </Box>
            </Paper>
          )}
          {snapshot.complianceInfo.intersectionUpcSet.length > 0 && (
            <Paper
              sx={{
                p: 4,
                flex: 1,
                border: `10px solid ${Colors.CORRECT}`,
              }}
            >
              <Typography variant="h5">
                {t('snapshotCompliance.correct', {
                  count: snapshot.complianceInfo.intersectionUpcSet.length,
                })}
              </Typography>
              <Box component="ul" sx={complianceProductListSx}>
                {Object.entries(
                  snapshot.complianceInfo.intersectionUpcSet.reduce(
                    (acc, item) => {
                      acc[item] = (acc[item] || 0) + 1
                      return acc
                    },
                    {} as Record<string, number>,
                  ),
                ).map(([upc, count]) => {
                  const product = productsByUpc[upc]
                  const label = getMissingProductLabel(product, upc)
                  return (
                    <ComplianceProductListItem
                      key={upc}
                      count={count}
                      label={label}
                      imageUrl={getProductImageUrl(product)}
                    />
                  )
                })}
              </Box>
            </Paper>
          )}
          {(snapshot.complianceInfo.nonFlexExtraBoundingBoxes.length > 0 ||
            snapshot.complianceInfo.flexExtraBoundingBoxes.length > 0) && (
            <Paper
              sx={{
                p: 4,
                flex: 1,
                border: `10px solid ${Colors.EXTRA}`,
              }}
            >
              {snapshot.complianceInfo.nonFlexExtraBoundingBoxes.length > 0 && (
                <>
                  <Typography variant="h5">
                    {t('snapshotCompliance.extra', {
                      count:
                        snapshot.complianceInfo.nonFlexExtraBoundingBoxes
                          .length,
                    })}
                  </Typography>
                  <Box component="ul" sx={complianceProductListSx}>
                    {Object.entries(
                      snapshot.complianceInfo.nonFlexExtraBoundingBoxes.reduce(
                        (acc, item) => {
                          if (!item.classifiedLabel) return acc
                          acc[item.classifiedLabel] =
                            (acc[item.classifiedLabel] || 0) + 1
                          return acc
                        },
                        {} as Record<string, number>,
                      ),
                    ).map(([upc, count]) => {
                      const product = productsByUpc[upc]
                      const label = getMissingProductLabel(product, upc)

                      return (
                        <ComplianceProductListItem
                          key={upc}
                          count={count}
                          label={label}
                          imageUrl={getProductImageUrl(product)}
                        />
                      )
                    })}
                  </Box>
                </>
              )}
              {snapshot.complianceInfo.flexExtraBoundingBoxes.length > 0 && (
                <>
                  <Typography variant="h5">
                    {t('snapshotCompliance.flexExtra', {
                      count:
                        snapshot.complianceInfo.flexExtraBoundingBoxes.length,
                    })}
                  </Typography>
                  <Box component="ul" sx={complianceProductListSx}>
                    {Object.entries(
                      snapshot.complianceInfo.flexExtraBoundingBoxes.reduce(
                        (acc, item) => {
                          if (!item.classifiedLabel) return acc
                          acc[item.classifiedLabel] =
                            (acc[item.classifiedLabel] || 0) + 1
                          return acc
                        },
                        {} as Record<string, number>,
                      ),
                    ).map(([upc, count]) => {
                      const product = productsByUpc[upc]
                      const label = getMissingProductLabel(product, upc)

                      return (
                        <ComplianceProductListItem
                          key={upc}
                          count={count}
                          label={label}
                          imageUrl={getProductImageUrl(product)}
                        />
                      )
                    })}
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default SnapshotCompliance
